import { tr } from "../i18n/LanguageContext";
import React, { useState, useEffect, useCallback } from "react";
import { MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { countries, getCitiesForCountry } from "../data/mock";

const LocationFilter = ({ onLocationChange, resetTrigger = 0, initialCountry = "Все страны", initialCity = "Все города", isOnlineActive = false, className }) => {
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [selectedCity, setSelectedCity] = useState(initialCity);
  const [availableCities, setAvailableCities] = useState(["Все города"]);
  const [previousCountry, setPreviousCountry] = useState(initialCountry);
  const [previousCity, setPreviousCity] = useState(initialCity);

  // Handle online toggle state changes
  useEffect(() => {
    if (isOnlineActive) {
      // Store current selections before switching to online
      if (selectedCountry !== "Все страны" || selectedCity !== "Все города") {
        setPreviousCountry(selectedCountry);
        setPreviousCity(selectedCity);
      }
      // Set to online mode
      setSelectedCountry("Все страны");
      setSelectedCity("Онлайн");
    } else {
      // Restore previous selections when deactivating online
      if (selectedCity === "Онлайн") {
        setSelectedCountry(previousCountry);
        setSelectedCity(previousCity);
      }
    }
  }, [isOnlineActive]);

  // Reset all states when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      setSelectedCountry("Все страны");
      setSelectedCity("Все города");
      setAvailableCities(["Все города"]);
      setPreviousCountry("Все страны");
      setPreviousCity("Все города");
    }
  }, [resetTrigger]);

  // Memoize the callback to prevent infinite re-renders
  const handleLocationChangeCallback = useCallback((location) => {
    if (onLocationChange) {
      onLocationChange(location);
    }
  }, [onLocationChange]);

  // Update available cities when country changes
  useEffect(() => {
    const cities = getCitiesForCountry(selectedCountry);
    setAvailableCities(cities);
    
    // Reset city selection when country changes
    if (selectedCountry !== "Все страны") {
      setSelectedCity("Все города");
    }
  }, [selectedCountry]);

  // Notify parent component of location changes
  useEffect(() => {
    handleLocationChangeCallback({
      country: selectedCountry,
      city: selectedCity
    });
  }, [selectedCountry, selectedCity, handleLocationChangeCallback]);

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Country Filter */}
      <div>
        <Select 
          value={selectedCountry} 
          onValueChange={handleCountryChange}
          disabled={isOnlineActive}
        >
          <SelectTrigger 
            aria-label="Select country"
            className={`bg-white/80 border-emerald-200 focus:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 ${
              isOnlineActive ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className={`h-4 w-4 flex-shrink-0 ${isOnlineActive ? 'text-gray-400' : 'text-emerald-600'}`} />
              <span className="truncate">
                {isOnlineActive ? tr("Все страны (отключено)") : tr(selectedCountry)}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {[countries[0], ...countries.slice(1).sort((a, b) => tr(a).localeCompare(tr(b)))].map(country => (
              <SelectItem key={country} value={country}>
                {tr(country)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* City Filter - Show only if a specific country is selected and online is not active */}
      {selectedCountry !== "Все страны" && !isOnlineActive && (
        <div className="flex-1">
          <Select value={selectedCity} onValueChange={handleCityChange}>
            <SelectTrigger 
              aria-label="Select city"
              className="bg-white/80 border-emerald-200 focus:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-4 h-4 flex items-center justify-center">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                </div>
                <span className="truncate">{tr(selectedCity)}</span>
              </div>
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {[...availableCities].sort((a, b) => (a === "Все города" ? -1 : b === "Все города" ? 1 : tr(a).localeCompare(tr(b)))).map(city => (
                <SelectItem key={city} value={city}>
                  {tr(city)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Online Status Display */}
      {isOnlineActive && (
        <div className="text-sm text-gray-600 bg-sky-50 px-3 py-2 rounded-lg border border-sky-200">
          <span className="font-medium">{tr("Режим онлайн:")} </span>
          <span className="text-sky-700">{tr("🌐 Местоположение не учитывается")}</span>
        </div>
      )}

      {/* Selected Location Display - Only show when not online and has selection */}
      {!isOnlineActive && selectedCountry !== "Все страны" && selectedCity !== "Все города" && (
        <div className="text-sm text-gray-600 bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-100">
          <span className="font-medium">{tr("Выбрано:")} </span>
          <span className="text-emerald-700">{tr(selectedCity)}, {tr(selectedCountry)}</span>
        </div>
      )}
      
      {!isOnlineActive && selectedCountry !== "Все страны" && selectedCity === "Все города" && (
        <div className="text-sm text-gray-600 bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-100">
          <span className="font-medium">{tr("Выбрано:")} </span>
          <span className="text-emerald-700">{tr("Все города в")} {tr(selectedCountry)}</span>
        </div>
      )}
    </div>
  );
};

export default LocationFilter;